import { apiFetch, apiFetchJson } from '@wundo/client/api.ts';
import type {
  Cohort,
  CohortDocument,
  CohortPrompt,
  CohortWithOrganisation,
  TrackParticipant,
} from '@/types/cohorts.ts';
import type { PaginatedResponse } from '@/types/pagination.ts';
import { PAGE_SIZE } from '@/lib/pageSize.ts';

export interface CohortListParams {
  page: number;
  /** Superadmins see every organisation's cohorts unless they name one. */
  organisationId?: string | null;
  /** Without one the server answers alphabetically by title. */
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export function listCohorts({
  page,
  organisationId,
  sortBy,
  sortOrder,
}: CohortListParams): Promise<PaginatedResponse<CohortWithOrganisation>> {
  const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
  if (organisationId) {
    params.set('organisation_id', organisationId);
  }
  if (sortBy) {
    params.set('sort_by', sortBy);
    params.set('sort_order', sortOrder ?? 'asc');
  }

  return apiFetchJson<PaginatedResponse<CohortWithOrganisation>>(`/cohorts?${params}`);
}

export interface CohortSearchParams {
  query: string;
  organisationId?: string | null;
  limit?: number;
  signal?: AbortSignal;
}

/**
 * The cohorts a picker offers: the first hundred whose organisation name,
 * cohort title or track title contains the typed text, ignoring case.
 */
export async function searchCohorts({
  query,
  organisationId,
  limit = 100,
  signal,
}: CohortSearchParams): Promise<CohortWithOrganisation[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (organisationId) {
    params.set('organisation_id', organisationId);
  }
  if (query.trim()) {
    params.set('query', query);
  }

  const { data } = await apiFetchJson<PaginatedResponse<CohortWithOrganisation>>(
    `/cohorts?${params}`,
    { signal },
  );
  return data;
}

export function fetchCohort(id: string): Promise<Cohort> {
  return apiFetchJson<Cohort>(`/cohorts/${id}`);
}

/**
 * What the form sends. Every optional field is nulled rather than dropped: an
 * emptied welcome message has to reach the backend as "no message", and an
 * absent key would leave the old one in place.
 */
export interface CohortWriteRequest {
  title: string;
  organisation_id: string;
  track_id: string | null;
  start_at: string | null;
  pilot: boolean;
  weeks: number;
  welcome_message: string | null;
}

export function createCohort(body: CohortWriteRequest): Promise<Cohort> {
  return apiFetchJson<Cohort>('/cohorts', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Only the fields that actually changed. */
export function updateCohort(
  id: string,
  body: Partial<CohortWriteRequest>,
): Promise<Cohort> {
  return apiFetchJson<Cohort>(`/cohorts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function deleteCohort(id: string): Promise<void> {
  await apiFetch(`/cohorts/${id}`, { method: 'DELETE' });
}

/** A track as the cohort form offers it: enough to pick one and name it. */
export interface CohortTrackOption {
  id: string;
  title: string;
}

/** The organisation's tracks, for the optional track on the cohort form. */
export async function listCohortTrackOptions(
  organisationId: string,
): Promise<CohortTrackOption[]> {
  const params = new URLSearchParams({
    organisation_id: organisationId,
    page: '1',
    limit: '100',
  });
  const { data } = await apiFetchJson<PaginatedResponse<CohortTrackOption>>(
    `/tracks?${params}`,
  );
  return data;
}

/* -- Participants ------------------------------------------------------- */

export interface CohortParticipantsParams {
  cohortId: string;
  page: number;
}

export function listCohortParticipants({
  cohortId,
  page,
}: CohortParticipantsParams): Promise<PaginatedResponse<TrackParticipant>> {
  const params = new URLSearchParams({
    cohort_id: cohortId,
    page: String(page),
    limit: String(PAGE_SIZE),
  });
  return apiFetchJson<PaginatedResponse<TrackParticipant>>(
    `/track-participants?${params}`,
  );
}

export async function addCohortParticipant(
  cohortId: string,
  userId: string,
): Promise<void> {
  await apiFetchJson<TrackParticipant>('/track-participants', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, cohort_id: cohortId }),
  });
}

export async function removeCohortParticipant(participantId: string): Promise<void> {
  await apiFetch(`/track-participants/${participantId}`, { method: 'DELETE' });
}

/* -- Documents ---------------------------------------------------------- */

/** The pivot rows: which of the organisation's documents this cohort links. */
export function listCohortDocuments(cohortId: string): Promise<CohortDocument[]> {
  return apiFetchJson<CohortDocument[]>(`/cohort-documents?cohort_id=${cohortId}`);
}

export async function linkCohortDocument(
  cohortId: string,
  documentId: string,
): Promise<void> {
  await apiFetchJson<CohortDocument>('/cohort-documents', {
    method: 'POST',
    body: JSON.stringify({ cohort_id: cohortId, document_id: documentId }),
  });
}

export async function unlinkCohortDocument(cohortDocumentId: string): Promise<void> {
  await apiFetch(`/cohort-documents/${cohortDocumentId}`, { method: 'DELETE' });
}

/* -- Prompt ------------------------------------------------------------- */

/** The four lists a cohort may add to the system prompt. */
export interface CohortPromptFields {
  purpose: string[];
  audience: string[];
  guardrails: string[];
  examples: string[];
}

/** Null when this cohort has never had one — not an error. */
export function fetchCohortPrompt(cohortId: string): Promise<CohortPrompt | null> {
  return apiFetchJson<CohortPrompt | null>(`/cohort-prompts?cohort_id=${cohortId}`);
}

export function createCohortPrompt(
  cohortId: string,
  fields: CohortPromptFields,
): Promise<CohortPrompt> {
  return apiFetchJson<CohortPrompt>('/cohort-prompts', {
    method: 'POST',
    body: JSON.stringify({ cohort_id: cohortId, ...fields }),
  });
}

export function updateCohortPrompt(
  id: string,
  fields: CohortPromptFields,
): Promise<CohortPrompt> {
  return apiFetchJson<CohortPrompt>(`/cohort-prompts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(fields),
  });
}

export async function deleteCohortPrompt(id: string): Promise<void> {
  await apiFetch(`/cohort-prompts/${id}`, { method: 'DELETE' });
}

/* -- Users -------------------------------------------------------------- */

/*
 * Reads the cohort pages make about somebody else's domain: the participants
 * tab picks a user to enrol. They live here until the users domain lands its
 * own lib, and then they move there.
 */

/** The cache key of a cohort's participants; each page adds its number. */
export function cohortParticipantsKey(cohortId: string): string[] {
  return ['cohorts', cohortId, 'participants'];
}
